import type { IAutoCompleteSiteItem } from "app/@types/autocomplete";
import defaultFetchServiceOverride from "app/common/services/salesFetchService";
import type { ExcelDownloadClient } from "app/components/React/ExcelButton/ExcelClientDownload";
import type { ISiteTag } from "app/pages/sales-intelligence/domain/leads/types/website";
import createAdvancedSearchFilteringApiService from "app/pages/sales-intelligence/modules/advanced-search-filtering/services/api/apiService";
import { createAppsSearchApiService } from "app/pages/sales-intelligence/modules/apps-search/services/api";
import createAppsSearchFiltersApiService from "app/pages/sales-intelligence/modules/apps-search-filters/services/api/factory";
import createAccountsCrmExportApiService from "app/pages/sales-intelligence/modules/crm-export/services/api/apiService";
import createCrmIntegrationApiService from "app/pages/sales-intelligence/modules/crm-integration/services/api/factory";
import createEnrichmentApiService from "app/pages/sales-intelligence/modules/enrichment/services/api/enrichmentApiService";
import createIndustryApiService from "app/pages/sales-intelligence/modules/industry/services/api/factory";
import createLeadsSearchApiService from "app/pages/sales-intelligence/modules/leads-search/services/api/apiService";
import createLeadsSearchFiltersApiService from "app/pages/sales-intelligence/modules/leads-search-filters/services/api/apiService";
import createListColumnsApiService from "app/pages/sales-intelligence/modules/lists/services/api/listColumnsApiService";
import createCrmListPageAPIService from "app/pages/sales-intelligence/modules/lists/sub-modules/crm-list/services/api/crmListPageApiService";
import createListPageAPIService from "app/pages/sales-intelligence/modules/lists/sub-modules/my-list/services/api/listPageApiService";
import { createSuggestionsApiService } from "app/pages/sales-intelligence/modules/suggestions/services/suggestionsApiService";
import createTechnologyApiService from "app/pages/sales-intelligence/modules/technology/services/api/factory";
import createAdvancedSearchPageApiService from "app/pages/sales-intelligence/pages/advanced-search/services/api/apiService";
import createFindAppsApiService from "app/pages/sales-intelligence/pages/find-apps/services/api/apiService";
import { createOpportunityListPageApiService } from "app/pages/sales-intelligence/pages/opportunity-list/services/api/apiService";
import createAccountSettingsApiService from "app/pages/sales-intelligence/services/api/account-settings/factory";
import createContactAdminApiService from "app/pages/sales-intelligence/services/api/contactAdminApiService";
import { createContactsApiService } from "app/pages/sales-intelligence/sub-modules/contacts-tab/services/contactsApiService";
import createIntentApiService from "app/pages/sales-intelligence/sub-modules/intent/services/api/apiService";
import createOnboardingApiService from "app/pages/sales-intelligence/sub-modules/onboarding/services/api/apiService";
import type { GetRequestOptions, IFetchService, PostConfigObject } from "app/services/fetchService";
import { fetchSimilarSitesQuery, fetchSiteInfoQuery } from "app/services/sitesService/queries/sitesQueries";
import type { SiteInfoResponse } from "app/services/sitesService/types";

import { createFeedApiService } from "../../workspace/sales/sub-modules/feed/services/feedApiService";
import { createWorkspaceSignalsApiService } from "../../workspace/sales/sub-modules/signals/services/signalsApiService";
import { createAiAgentApiService } from "../modules/ai-agent/services/api/aiAgentService";
import createCompanyAnalysisApiService from "../modules/company-analysis/services/api/apiService";
import createExtensionManagementApiService from "../modules/extension-management/services/api/factory";
import createPlaybooksApiService from "../modules/playbooks/services/api/playbooksApiService";
import createQuotaApiService from "../modules/quota/services/api/quotaApiService";
import createSalesloftApiService from "../modules/salesloft/services/api/salesloftApiService";
import createTeamsApiService from "../modules/teams/services/api/teamsApiService";
import createHomePageAPIService from "../pages/home/services/api/homePageApiService";
import createCompetitiveLandscapeApiService from "../sub-modules/account-overview/competitive-landscape/services/api/apiService";
import createAccountOverviewApiService from "../sub-modules/account-overview/shared/services/api/apiService";
import createAdvancedSearchApiService from "../sub-modules/advanced-search/services/api/apiService";
import createAssignToUsersApiService from "../sub-modules/assign-to-users/services/api/factory";
import createContactsSearchApiService from "../sub-modules/contacts-search/services/api/apiService";
import type { ContactIssuePayload } from "../sub-modules/contacts-search/types/common";
import createContactsSearchProfilesApi from "../sub-modules/contacts-search-profiles/services/contactsSearchProfilesApi";
import { createManagementApiService } from "../sub-modules/management/store/services";
import { createDynamicsExportSettingsApiService } from "../sub-modules/management/store/services/dynamics/export";
import { createHubspotExportSettingsApiService } from "../sub-modules/management/store/services/hubspot/export";
import { createSfExportSettingsApiService } from "../sub-modules/management/store/services/salesforce/export";
import { createOpportunitiesApiService } from "../sub-modules/opportunities/services/api";
import createSignalsApiService from "../sub-modules/signals/services/api/factory";

import { SalesIntelligenceExcelClient } from "./excelClient";

const DEFAULT_SIMILAR_SITES_FETCH_SIZE = 20;

const createSalesApiService = (fetchService: IFetchService, excelDownloadClient: ExcelDownloadClient) => {
    return {
        advancedSearchFiltering: createAdvancedSearchFilteringApiService(fetchService),
        assignToUsers: createAssignToUsersApiService(fetchService),
        feed: createFeedApiService(fetchService),
        signals: createSignalsApiService(fetchService, excelDownloadClient),
        workspaceSignals: createWorkspaceSignalsApiService(fetchService),
        opportunities: createOpportunitiesApiService(fetchService, excelDownloadClient),
        opportunityListPage: createOpportunityListPageApiService(fetchService, excelDownloadClient),
        advancedSearch: createAdvancedSearchApiService(fetchService),
        leadsSearch: createLeadsSearchApiService(fetchService),
        leadsSearchFilters: createLeadsSearchFiltersApiService(fetchService),
        crmIntegration: createCrmIntegrationApiService(fetchService),
        industry: createIndustryApiService(fetchService),
        technology: createTechnologyApiService(fetchService),
        appsSearchFilters: createAppsSearchFiltersApiService(fetchService),
        advancedSearchPage: createAdvancedSearchPageApiService(fetchService, excelDownloadClient),
        contactsSearch: createContactsSearchApiService(fetchService, excelDownloadClient),
        contactsSearchProfiles: createContactsSearchProfilesApi(fetchService),
        accountsCrmExport: createAccountsCrmExportApiService(fetchService),
        accountOverview: createAccountOverviewApiService(fetchService, excelDownloadClient),
        companyAnalysis: createCompanyAnalysisApiService(fetchService, excelDownloadClient),
        management: createManagementApiService(fetchService),
        contacts: createContactsApiService(fetchService, excelDownloadClient),
        //TODO: both Export services should be included in Management
        salesForceExportSettings: createSfExportSettingsApiService(fetchService),
        hubspotExportSettings: createHubspotExportSettingsApiService(fetchService),
        dynamicsExportSettings: createDynamicsExportSettingsApiService(fetchService),
        competitiveLandscape: createCompetitiveLandscapeApiService(fetchService, excelDownloadClient),
        intent: createIntentApiService(fetchService),
        home: createHomePageAPIService(fetchService),
        onboarding: createOnboardingApiService(fetchService),
        enrichment: createEnrichmentApiService(fetchService),
        extensionManagement: createExtensionManagementApiService(fetchService),
        lists: createListPageAPIService(fetchService),
        crmList: createCrmListPageAPIService(fetchService),
        findApps: createFindAppsApiService(fetchService, excelDownloadClient),
        appsSearch: createAppsSearchApiService(fetchService),
        aiAgent: createAiAgentApiService(fetchService),
        suggestions: createSuggestionsApiService(fetchService),
        listColumns: createListColumnsApiService(fetchService),
        quota: createQuotaApiService(fetchService),
        teams: createTeamsApiService(fetchService),
        playbooks: createPlaybooksApiService(fetchService),
        salesloft: createSalesloftApiService(fetchService),
        contactAdmin: createContactAdminApiService(fetchService),
        accountSettings: createAccountSettingsApiService(fetchService),

        fetchSimilarWebsites(domain: string, size = DEFAULT_SIMILAR_SITES_FETCH_SIZE): Promise<unknown[]> {
            return fetchSimilarSitesQuery(domain, { limit: size });
        },

        /**
         * Fetches info (like icons, category etc.) about given domain
         * TODO: Consider moving this to some common place maybe
         * @param domain
         */
        fetchWebsiteInfo(domain: string): Promise<SiteInfoResponse | null> {
            return fetchSiteInfoQuery({
                includeCrossData: false,
                mainDomainOnly: true,
                keys: domain,
            });
        },

        postContactIssue(payload: ContactIssuePayload): Promise<void> {
            return fetchService.post(`/sales-api/contact-issues`, payload);
        },

        fetchAutocompleteCompanies(
            params: { domains: readonly string[]; size?: number; withDataCredits?: boolean },
            options?: Readonly<GetRequestOptions>,
        ): Promise<IAutoCompleteSiteItem[]> {
            return fetchService.post("/sales-api/autocomplete/companies", params, options);
        },

        getAutocompleteCompanies(
            params: { term: string; size?: number; withDataCredits?: boolean },
            options?: Readonly<GetRequestOptions>,
        ): Promise<IAutoCompleteSiteItem[]> {
            return fetchService.get("/sales-api/autocomplete/companies", params, options);
        },

        fetchSiteTags(term: string, size: number, config?: PostConfigObject): Promise<readonly ISiteTag[]> {
            return fetchService.get("sales-api/autocomplete/tags", { term, size }, config);
        },
    };
};

export default createSalesApiService(defaultFetchServiceOverride, SalesIntelligenceExcelClient);
